import React from 'react';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';

import { handleAuthError } from '@auth/AuthErrorHandler';
import {
  signInWithEmailAndPassword,
  validateUserEmail,
  validateUserPassword,
} from '@auth/EmailAndPassword';
import { TextDivider } from '@components/Divider';
import {
  AppleSignInButton,
  FacebookSignInButton,
  GoogleSignInButton,
} from '@components/FederatedSignInButton';
import { createStringValidator, submitFormSafely, useAppForm } from '@components/Form';
import { BackButton } from '@components/IconButton';
import ScreenContainer from '@components/ScreenContainer';
import { Caption, Headline, Paragraph } from '@components/Typography';
import { AuthRoute } from '@navigation/Routes';
import { LoginScreenProps } from '@navigation/Types';

type LoginFormValues = {
  email: string;
  password: string;
};

export default function LoginScreen({ navigation }: LoginScreenProps) {
  const passwordRef = React.useRef<TextInput>(null);

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    } satisfies LoginFormValues,
    onSubmit: async ({ value }) => {
      try {
        await signInWithEmailAndPassword(value.email, value.password);
      } catch (error) {
        handleAuthError(error);
        throw error;
      }
    },
  });

  const onLoginPressed = () => submitFormSafely(form);

  const renderEmailInput = () => (
    <form.AppField name="email" validators={{ onChange: createStringValidator(validateUserEmail) }}>
      {(field) => (
        <field.EmailInput
          label="Email"
          returnKeyType="next"
          onSubmitEditing={() => passwordRef.current?.focus()}
          testID="screens.LoginScreen.TextInput.Email"
        />
      )}
    </form.AppField>
  );

  const renderPasswordInput = () => (
    <form.AppField
      name="password"
      validators={{ onChange: createStringValidator(validateUserPassword) }}>
      {(field) => (
        <field.PasswordInput
          inputRef={passwordRef}
          label="Password"
          returnKeyType="done"
          onSubmitEditing={onLoginPressed}
          testID="screens.LoginScreen.TextInput.Password"
        />
      )}
    </form.AppField>
  );

  const renderForgotPasswordLink = () => (
    <View style={styles.forgotPassword}>
      <TouchableOpacity
        onPress={() => navigation.navigate(AuthRoute.ResetPassword)}
        testID="screens.LoginScreen.Button.ForgotPassword">
        <Caption>Forgot your password?</Caption>
      </TouchableOpacity>
    </View>
  );

  const renderFederatedSignInButtons = () => (
    <>
      <TextDivider text="OR" />
      <AppleSignInButton />
      <GoogleSignInButton />
      <FacebookSignInButton />
    </>
  );

  const renderRegisterLink = () => (
    <View style={styles.row}>
      <Paragraph>{"Don't have an account? "}</Paragraph>
      <TouchableOpacity
        onPress={() => navigation.replace(AuthRoute.Register)}
        testID="screens.LoginScreen.Button.Register">
        <Paragraph style={styles.link}>Sign up</Paragraph>
      </TouchableOpacity>
    </View>
  );

  return (
    <form.AppForm>
      <ScreenContainer scrollable testID="screens.LoginScreen.Background">
        <BackButton onPress={navigation.goBack} testID="screens.LoginScreen.BackButton" />
        <View style={styles.container}>
          <Headline style={styles.title}>Sign in to Joinstar</Headline>
          {renderEmailInput()}
          {renderPasswordInput()}
          {renderForgotPasswordLink()}
          <form.SubmitBigButton mode="contained" testID="screens.LoginScreen.Button.Login">
            Log In
          </form.SubmitBigButton>
          {renderFederatedSignInButtons()}
          {renderRegisterLink()}
        </View>
      </ScreenContainer>
    </form.AppForm>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  forgotPassword: {
    alignItems: 'flex-end',
    marginBottom: 8,
    width: '100%',
  },
  link: {
    fontWeight: 'bold',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 4,
  },
  title: {
    paddingBottom: 5,
    textAlign: 'center',
  },
});
